import React from 'react';

const START_ANGLE = -Math.PI / 2;
const FULL_TURN = Math.PI * 2;
const STROKE_WIDTH = 1;
const MAX_INNER_RATIO = 0.95;

// Legacy `.infovis-mini { padding: 3px }` — the gap that makes the plate read as a badge around the ring.
const PLATE_INSET = 3;

export interface MiniDonutSegment {
	name: string;
	value: number;
	color: string;
	tooltip?: string;
}

export interface MiniDonutProps {
	segments: MiniDonutSegment[];
	size?: number;
	innerRatio?: number;
	title?: string;
	plate?: string;
	borderWidth?: number;
}

const fmt = (value: number) => value.toFixed(3);

export const MiniDonut: React.FC<MiniDonutProps> = ({ segments, size = 28, innerRatio = 0.3, title, plate, borderWidth = STROKE_WIDTH }) => {
	const values = segments.map(s => (Number.isFinite(s.value) && s.value > 0 ? s.value : 0));
	const total = values.reduce((sum, v) => sum + v, 0);

	if (!segments.length || total <= 0) return null;

	const centre = size / 2;
	// Inset by half the stroke, else the outer edge is clipped by the viewBox.
	const outerR = centre - borderWidth / 2 - (plate ? PLATE_INSET : 0);
	const innerR = Math.min(Math.max(innerRatio, 0), MAX_INNER_RATIO) * outerR;

	const at = (angle: number, radius: number) =>
		`${fmt(centre + Math.cos(angle) * radius)} ${fmt(centre + Math.sin(angle) * radius)}`;

	const arc = (radius: number, large: number, sweepFlag: number, angle: number) =>
		`A ${fmt(radius)} ${fmt(radius)} 0 ${large} ${sweepFlag} ${at(angle, radius)}`;

	const slice = (from: number, sweep: number) => {
		if (sweep >= FULL_TURN - 1e-9) {
			// Two half arcs — one A command cannot close a full turn; the rings wind opposite ways so nonzero fill leaves the hole empty.
			const half = from + Math.PI;
			const ring = `M ${at(from, outerR)} ${arc(outerR, 1, 1, half)} ${arc(outerR, 1, 1, from)} Z`;
			if (innerR <= 0) return ring;
			return `${ring} M ${at(from, innerR)} ${arc(innerR, 1, 0, half)} ${arc(innerR, 1, 0, from)} Z`;
		}

		const to = from + sweep;
		const large = sweep > Math.PI ? 1 : 0;
		const outerArc = `M ${at(from, outerR)} ${arc(outerR, large, 1, to)}`;
		if (innerR <= 0) return `${outerArc} L ${fmt(centre)} ${fmt(centre)} Z`;
		return `${outerArc} L ${at(to, innerR)} ${arc(innerR, large, 0, from)} Z`;
	};

	let cursor = START_ANGLE;
	const paths = values.map(value => {
		const from = cursor;
		const sweep = (value / total) * FULL_TURN;
		cursor += sweep;
		return slice(from, sweep);
	});

	return (
		<svg
			data-testid="mini-donut"
			width={size}
			height={size}
			viewBox={`0 0 ${size} ${size}`}
			role={title ? 'img' : undefined}
			aria-label={title}
			style={{ display: 'block', flexShrink: 0 }}
		>
			{plate && <circle data-testid="mini-donut-plate" cx={centre} cy={centre} r={centre} fill={plate} />}
			{segments.map((segment, i) => (
				<path
					key={`${segment.name}-${i}`}
					data-testid="mini-donut-slice"
					data-name={segment.name}
					d={paths[i]}
					fill={segment.color}
					stroke="var(--mg-bg)"
					strokeWidth={borderWidth}
				>
					{segment.tooltip ? <title>{segment.tooltip}</title> : null}
				</path>
			))}
		</svg>
	);
};
